import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { register, SwiperContainer } from 'swiper/element/bundle'
import { SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, EffectFlip, Autoplay } from 'swiper/modules';

// Components
import LoadingCardsComponent from "./LoadingCards/LoadingCards";
import ErrorMsgComponent from "GeneralComponents/GenericErrorMessage";
import TabsCarousel from "./Tabs/TabsCarousel";
import CarouselItemComponent from './Carousel_ItemComponent';
import NavigationBtns from './Carousel_NavigationBtns';
import PaginationMenu from './Carousel_PaginationMenu';
import ComponentHeader from "GeneralComponents/ComponentHeader";
import ComponentBackground from "GeneralComponents/ComponentBackground"
import CustomLinkButton from 'GeneralComponents/CustomLinkButton';

// Consts
import Consts from "Interfaces/Consts";
import { API_URL, CAROUSEL_ITEMS_BY_DEVICE, CAROUSEL_TYPES, LINKS_VIEW_MODE } from './Carousel_ConfigVars.interface';

// Types and Utils
import CarouselItem, { CarouselApiItem, CarouselComponentProps as Props, UmbracoApiListKeys } from "./Carousel.interface";
import useApiList from './useApiList';
import { BackColor, Link } from "Interfaces/general.interface"; 
import Helper from "Interfaces/Helper";
import { calculateItemsNumber, cardNumberClass, getEventCarouselType, getScreenSizes } from './Carousel_Helper.interface';

// Import Swiper styles
import '/node_modules/Swiper/swiper-bundle.min.css';
import '/node_modules/swiper/modules/pagination.min.css';
import '/node_modules/swiper/modules/navigation.min.css';
import '/node_modules/swiper/modules/effect-flip.min.css';
import '/node_modules/swiper/modules/a11y.min.css';
import './Carousel.css';

register();

const Carousel: React.FC<Props> = (props) => {

    // STATIC VARS
    const { componentBGImage, componentBGMobileImage, currentPageId, bgImgOpacity, componentDescription, componentId, componentQuery, componentTitle } = props;
    const { isTablet, isLaptop, is1200Desktop } = getScreenSizes();
    const mediaType = window.matchMedia("(min-width: 1024px)");
    const dir = getComputedStyle(document.documentElement).direction;
    const carouselType = props.carouselType || CAROUSEL_TYPES.LINKS;
    const initItemsToShow = setDefaultItemsToShowByView((+props.itemstoshow || 0), carouselType);
    const backGround: BackColor | null = props.backColor ? JSON.parse(props.backColor) : null;
    const link = Helper.convertServerLinkItemToClientLinkItem(props.link);
    let isEventLisstenerBound = false;
    let itemsNumber = calculateItemsNumber();

    const ApiMappingHelper = useApiList();

    // STATE VARS:
    const [items, setItems] = useState<CarouselItem[]>([]);
    const [loadingItems, setLoadingItems] = useState<number[]>([]);
    const [itemstoshow, setItemstoshow] = useState<number>(initItemsToShow);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [showErrorComponent, setShowErrorComponent] = useState<boolean>(false);
    const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
    const [isApiList, setIsApiList] = useState<boolean>(false);
    const [apiErrorMsg, setApiErrorMsg] = useState<string>('');
    const [lightBg, setLightBg] = useState<boolean>(props.light == "true");
    const [flipAll, setFlipAll] = useState<boolean>(true);
    const [linksViewMode, setLinksViewMode] = useState<string>('');
    const [isAutoplayActive, setIsAutoplayActive] = useState<boolean>(true);
    const [isSwiperInit, setIsSwiperInit] = useState<boolean>(false);

    // FUNCTIOS:
    const handleApiListDisplay = async (apiList: UmbracoApiListKeys[]) => {

        const isSupportedCarouselType = (carouselType === CAROUSEL_TYPES.LINKS);
        const isValidApiUrl = ApiMappingHelper.checkIfUrlIsSupported(apiList);

        if (!isSupportedCarouselType || !isValidApiUrl) return;

        // configure number of loading items to display:
        const placeholderItemsToShow = [1, (isTablet ? 2 : 0), (is1200Desktop ? 3 : 0)].filter(placeholderCard => !!placeholderCard);
        // update on loading
        setIsLoaded(true);
        setIsApiList(true);
        setIsApiLoading(true);
        setLoadingItems(placeholderItemsToShow);

        const itemsRes: CarouselApiItem[] | string = await ApiMappingHelper.handleDataFromApi(apiList);
        const hasError = typeof itemsRes === 'string';

        if (hasError) {
            setShowErrorComponent(true)
            setApiErrorMsg(itemsRes as string)
            return;
        }

        // configure number of items to display (max 3 items)
        const itemsNumber = itemstoshow;
        const itemsToDisplay: number = itemsNumber > 3 ? 3 : itemsNumber;

        setItems(itemsRes as CarouselApiItem[])
        setIsApiLoading(false);
        setItemstoshow(itemsToDisplay);

    }

    const getItems = async () => {

        try {

            const reqParams = { API_URL, queryId: componentQuery, pageId: currentPageId };
            const serverRes: CarouselItem[] | UmbracoApiListKeys[] = await Helper.fetchData(reqParams);
            const isApiList = ApiMappingHelper.checkIfListIsApiMappingOrNormal(serverRes);

            if (isApiList) {
                await handleApiListDisplay(serverRes as UmbracoApiListKeys[]);
            }
            else {
                const data = serverRes as CarouselItem[];
                const isMiniNews = carouselType === CAROUSEL_TYPES.MINI_NEWS;
                const itemsToRender = isMiniNews ? data.reverse() : data;
                const updatedLinksViewMode = updateLinksViewMode(itemsToRender);

                setLinksViewMode(updatedLinksViewMode);
                setItems(itemsToRender);
                setIsLoaded(true);
            }
        }
        catch (error) {
            setIsLoaded(true);
            setError(true);
        }

        setBGClassByView();
    }

    const addNavigationEnterEvent = (swiper, prevClass: string, nextClass: string) => {
        const prevButton = document.querySelector(prevClass + componentId)
        const nextButton = document.querySelector(nextClass + componentId)

        if (prevButton != null) { prevButton.setAttribute("id", "banner-prev-btn-" + componentId) }
        if (nextButton != null) { nextButton.setAttribute("id", "banner-next-btn-" + componentId) }
        
        if (nextButton && !((nextButton as any).eventListener)) {
            nextButton.addEventListener("keydown", function (event: KeyboardEvent) {
                if (event.key === "Enter") swiper.slideNext()
            })
        }
        if (prevButton && !((prevButton as any).eventListener)) {
            prevButton.addEventListener("keydown", function (event: KeyboardEvent) {
                if (event.key === "Enter") swiper.slidePrev()
            })
        }
    }

    const addEventListeners = () => {
        // What is the use for '["swiper"]'??
        const swiper = getSwiperElement()["swiper"];

        if (swiper && carouselType === CAROUSEL_TYPES.MINI_NEWS) {
            addNavigationEnterEvent(swiper, ".btn-prev-", ".btn-next-")
        } else if (swiper && carouselType !== CAROUSEL_TYPES.TABS && isLaptop) {
            addNavigationEnterEvent(swiper,".btn-right-", ".btn-left-")
        }

        if (carouselType === CAROUSEL_TYPES.FLIPPINGS) {
            const nextButton = swiper.hostEl.shadowRoot.querySelector(".swiper-button-next")
            const prevButton = swiper.hostEl.shadowRoot.querySelector(".swiper-button-prev")

            if (nextButton != null) { nextButton.setAttribute("id", "banner-next-btn-" + componentId) }
            if (prevButton != null) { prevButton.setAttribute("id", "banner-prev-btn-" + componentId) }

            const flipAll = () => { setFlipAll(true) }

            if (nextButton) {
                nextButton.addEventListener("click", flipAll)
                prevButton.addEventListener("click", flipAll)
                nextButton.addEventListener("keyup", focusOnActiveSlides)
                prevButton.addEventListener("keyup", focusOnActiveSlides)
            }

            try {
                allowTabOnActiveSlides(false);
                const swiperElement = getSwiperElement()
                swiperElement.querySelectorAll(".mst-card > .flip-box-front a, .mst-card > .flip-box-front .flip-link").forEach(x => x.addEventListener("keydown", flipOnActiveSlides))
            }
            catch (e) { }
        }
    }
    /*check if navigation buttons should appear (if there is more items than slidesPerView)
    because with the new navigation buttons Swiper dont hide them automatically*/
    const isNavigation = () => {

        let device = "Mobile"
        if (isTablet) device = "Tablet"
        if (isLaptop) device = "Laptop"
        if (is1200Desktop) device = "Desktop";

        const itemsToShow = getItemsToShowByDevice(device, itemstoshow > items.length ? items.length : itemstoshow)
        return items.length > itemsToShow
    }

    const getBreakpoints = () => {

        const totalItemsToShow = (itemstoshow > items.length) ? items.length : itemstoshow;

        const mobileItems = getItemsToShowByDevice("Mobile", totalItemsToShow);
        const tabletItems = getItemsToShowByDevice("Tablet", totalItemsToShow);
        const laptopItems = getItemsToShowByDevice("Laptop", totalItemsToShow);
        const desktopItems = getItemsToShowByDevice("Desktop", totalItemsToShow);

        const slidesForMobile = (carouselType === CAROUSEL_TYPES.EVENTS || carouselType === CAROUSEL_TYPES.FLIPPINGS) ? 'auto' : mobileItems;

        const breakpointsForCarousel = {
            0: { slidesPerView: slidesForMobile, slidesPerGroup: mobileItems },
            768: { slidesPerView: tabletItems, slidesPerGroup: tabletItems },
            1024: { slidesPerView: laptopItems, slidesPerGroup: laptopItems },
            1200: { slidesPerView: desktopItems, slidesPerGroup: desktopItems }
        }

        const breakpointsForMiniNewsCarousel = {
            0: { slidesPerView: 'auto', slidesPerGroup: 1 },
            768: { slidesPerView: tabletItems, slidesPerGroup: 1 },
            1024: { slidesPerView: laptopItems, slidesPerGroup: 1 },
            1200: { slidesPerView: desktopItems, slidesPerGroup: 1 }
        }
        return carouselType === CAROUSEL_TYPES.MINI_NEWS ? breakpointsForMiniNewsCarousel : breakpointsForCarousel
    }

    const getPagination = () => {

        const paginationForCarousel = {
            "clickable": true,
            "type": ((!mediaType.matches || (items.length / itemstoshow) > 5) ? "fraction" : "bullets")
        }
        const paginationForMininews = {
            "clickable": true,
            "type": ((!mediaType.matches || items.length + 1 - itemstoshow > 5) ? "fraction" : "bullets"),
        }
        return carouselType === CAROUSEL_TYPES.MINI_NEWS ? paginationForMininews : paginationForCarousel
    }

    const getNavigation = (swiperId) => {

        if (isNavigation()) {
            if (carouselType == CAROUSEL_TYPES.FLIPPINGS && !mediaType.matches) return false
            if (carouselType === CAROUSEL_TYPES.MINI_NEWS) return {
                nextEl: '.btn-next-' + swiperId,
                prevEl: '.btn-prev-' + swiperId
            }
            return isLaptop ? {
                nextEl: '.btn-left-' + swiperId,
                prevEl: '.btn-right-' + swiperId
            } : true
        }
        return false
    }

    const initSwiper = () => {

        const swiperEl = document.getElementById('mst-swiper-' + componentId) as SwiperContainer;

        if (!swiperEl) return;

        if (items.length > 0 && swiperEl["breakpoints"] == undefined) {
            const swiperParams = {
                a11y: { prevSlideMessage: Consts.prevSlideMessage, nextSlideMessage: Consts.nextSlideMessage },
                breakpoints: getBreakpoints(),
                modules: { Navigation, Pagination, A11y, Autoplay },
                navigation: getNavigation(componentId),
                pagination: getPagination(),
                rewind: (carouselType === CAROUSEL_TYPES.MINI_NEWS && isTablet),
                autoplay: carouselType === CAROUSEL_TYPES.MINI_NEWS && isTablet ? {delay:8000} : false,
                injectStyles: [`
                    .swiper-button-next, .swiper-button-prev {
                        &.swiper-button-disabled{
                            opacity: 0.67;
                        }
                        svg {
                            width: 12px;
                            height: 12px;  
                            margin-top:7px; 
                        }
                                          
                     }`,
                ],
                on: { slideChange: () => { allowTabOnActiveSlides(false) } }                            
            }

            const swiperParamsForTabs = {
                a11y: { prevSlideMessage: Consts.prevSlideMessage, nextSlideMessage: Consts.nextSlideMessage },
                modules: { Navigation, Pagination, A11y },
                navigation: items.length > itemsNumber,
                autoHeight: true,
                speed: 400,
                slidesPerView: 1,
                injectStyles: [`
                    .swiper-button-next svg, .swiper-button-prev svg {
                        display:none;
                    width: 10px;
                    height: 10px;  
                    margin-top:8px;                   
                    @media (min-width: 1024px) {
                        width: 16px;
                        height: 16px;
                        margin-top:12px;         
                    }}`
                ],
                on: {
                    slideChange: () => {
                        allowTabOnActiveSlides(false)
                    },
                    afterInit: (swiper) => {
                        setIsSwiperInit(true);
                        const swiperContainer = document.querySelector('#' + swiper.el.offsetParent.id);
                        const swiperBtnNext = swiperContainer.shadowRoot.querySelector("div[part='button-next']");
                        const swiperBtnPrev = swiperContainer.shadowRoot.querySelector("div[part='button-prev']");

                        if (swiperBtnNext != null) { swiperBtnNext.setAttribute("id", "banner-next-btn-" + swiper.el.offsetParent.id.split('-')[1]) }
                        if (swiperBtnPrev != null) { swiperBtnPrev.setAttribute("id", "banner-prev-btn-" + swiper.el.offsetParent.id.split('-')[1]) }
                    }
                }
            }

            if (carouselType === CAROUSEL_TYPES.TABS) Object.assign(swiperEl, swiperParamsForTabs)
            else Object.assign(swiperEl, swiperParams);
            swiperEl.initialize();
            allowTabOnActiveSlides(false);
        }

        const paginationMenu = document.getElementsByClassName('pagination-menu-' + componentId)[0]

        if (paginationMenu && swiperEl.shadowRoot.querySelector('.swiper-pagination')) {
            swiperEl.shadowRoot.querySelector('.swiper-pagination').classList.add("contents")
            paginationMenu.querySelector('.navigation-contianer').appendChild(swiperEl.shadowRoot.querySelector('.swiper-pagination'))
        }

        if (!isEventLisstenerBound) {
            addEventListeners()
            isEventLisstenerBound = true;
        }
    }

    const pauseNews = () => {

        const swiperObjectAutoplay = getSwiperElement()["swiper"].autoplay;
        setIsAutoplayActive(!isAutoplayActive)

        if (isAutoplayActive) {
            swiperObjectAutoplay.stop()
            swiperObjectAutoplay.paused = true
        }
        else {
            swiperObjectAutoplay.start()
            swiperObjectAutoplay.paused = false
        }
    }

    const getItemsToShowByDevice = (device, carouselItemsToShow) => {
        let itemsNumber = 4;
        try {
            itemsNumber = (carouselItemsToShow > 0 && carouselItemsToShow <= 5) ?
                CAROUSEL_ITEMS_BY_DEVICE[carouselItemsToShow][device] : CAROUSEL_ITEMS_BY_DEVICE["default"][device];
        }
        catch (ex) { }
        return itemsNumber;
    }

    const getLinksHasImages = () => (linksViewMode == LINKS_VIEW_MODE.image);

    function updateLinksViewMode(items: CarouselItem[]) {
        const hasImages = items.length > 0 && items[0].image.url;
        return hasImages ? LINKS_VIEW_MODE.image : LINKS_VIEW_MODE.icon;
    }

    function focusOnActiveSlides(e){
        if (e["code"] == "Enter") allowTabOnActiveSlides(false)
    }

    function flipOnActiveSlides(e){
        if (e["code"] == "Enter") allowTabOnActiveSlides(true)
    }

    function allowTabOnActiveSlides(isFlip: boolean){
        try {
            const swiperElement = getSwiperElement()
            const swiperObject = swiperElement["swiper"];
            swiperElement.querySelectorAll(".mst-card a").forEach(x => x["tabIndex"] = -1);
            swiperElement.querySelectorAll(".mst-card > .flip-box-front a, .mst-card > .flip-box-back a, .mst-card > .flip-box-front .flip-link, .mst-card > .flip-box-back .flip-link").forEach(x => x["tabIndex"] = -1);

            let lastInView = swiperObject.activeIndex + swiperObject.params.slidesPerView;
            if (lastInView > swiperObject.slides.length)
                lastInView = swiperObject.slides.length;
     
            for (let i = swiperObject.activeIndex; i < lastInView; i++) {
                swiperObject.slides[i].querySelectorAll(".mst-card a").forEach(x => allowTabIndexOnVisibleElement(x))
                swiperObject.slides[i].querySelectorAll(".mst-card > .flip-box-front a, .mst-card > .flip-box-front .flip-link").forEach(x => allowTabIndexOnVisibleElement(x))
            }
            swiperElement.querySelectorAll(".mst-card > .flip-box-back a, .mst-card > .flip-box-back .flip-link").forEach(x => x["tabIndex"] = -1);


            if (isFlip) {
                for (let i = swiperObject.activeIndex; i < lastInView; i++) {
                    swiperObject.slides[i].querySelectorAll(".mst-card > .flip-box-back a, .mst-card > .flip-box-back .flip-link").forEach(x => allowTabIndexOnVisibleElement(x))
                }
            }

            if (carouselType === CAROUSEL_TYPES.MINI_NEWS || carouselType === CAROUSEL_TYPES.EVENTS) {
                swiperElement.querySelectorAll(".event_carousel_item a,.miniNews_carousel_item a").forEach(x => x["tabIndex"] = -1);
                for (let i = swiperObject.activeIndex; i < lastInView; i++) {
                    swiperObject.slides[i].querySelectorAll(".link_placeholder > a").forEach(x => allowTabIndexOnVisibleElement(x))
                }
            }
        }
        catch (e) {}
    }

    function allowTabIndexOnVisibleElement(clickableElement){
        if (clickableElement["data-invisible"] != "true") {
            clickableElement["tabIndex"] = 0;
        }
    }

    function getSwiperElement() { return document.querySelector(".mst-swiper-" + componentId) };

    function getCarouselRoot(){
        const carouselRootEl = document.getElementById("Carousel-" + componentId);
        return carouselRootEl;
    }

    function setBGClassByView(){
        try {
            if (carouselType) {
                var carousel = getCarouselRoot()
                switch (carouselType) {
                    case CAROUSEL_TYPES.ICONS: carousel.classList.add("mst-icon-carousel"); break;
                    case CAROUSEL_TYPES.BOXES: carousel.classList.add("mst-box-carousel"); break;
                    case CAROUSEL_TYPES.LINKS: carousel.classList.add("mst-link-carousel"); break;
                    case CAROUSEL_TYPES.FLIPPINGS: carousel.classList.add("mst-flipping-carousel"); break;
                    case CAROUSEL_TYPES.TABS: carousel.classList.add("mst-tabs-carousel"); break;
                    case CAROUSEL_TYPES.EVENTS: carousel.classList.add("mst-events-carousel"); break;
                    case CAROUSEL_TYPES.MINI_NEWS: carousel.classList.add("mst-mininews-carousel"); break;
                    default: carousel.classList.add("mst-icon-carousel"); break;
                }
            }
        }
        catch (e) {

        }
    }

    function setDefaultItemsToShowByView(itemsToShow: number, carouselType: string){
        try {
            const defaultItemsPerView = {
                "Boxes": 4,
                "Links": 3,
                "Icons": 2,
                "Flippings": 2,
                "Events": 3,
                "MiniNews": 3,
                "Tabs": 5
            }

            const defaultNumber = defaultItemsPerView[carouselType];

            if (itemsToShow <= 0) return defaultNumber;

            //maximum 3
            if ( (carouselType === CAROUSEL_TYPES.EVENTS || carouselType === CAROUSEL_TYPES.MINI_NEWS)
                && itemsToShow > defaultNumber) {
                return defaultNumber
            } 
            
            if (itemsToShow <= defaultNumber + 1) return itemsToShow;

            return defaultNumber + 1 // maximum allowed
        }
        catch (e) { return 2; }
    }

    const getIconsHasImages = () => !items[0].icon && !!items[0].image.url;

    const sharedProps = {
        componentDescription,
        componentId,
        componentTitle,
        dir,
        carouselType,
        link,
        items,
        itemstoshow,
        flipAll,
        isAutoplayActive,
        lightBg,
        isApiList,
        isSwiperInit,
        itemsNumber
    }

    // LIFE-CYCLE Effects:
    useEffect(() => { getItems() }, []);

    useEffect(() => { initSwiper() }, [items]);

    if (!isLoaded || error) return null;

    // Render Tabs Carousel
    if (carouselType == CAROUSEL_TYPES.TABS) {

        return <TabsCarousel {...sharedProps} isLaptop={isLaptop} />

    };

    const displayCarouselNavigationBtns = (isLaptop && carouselType !== CAROUSEL_TYPES.MINI_NEWS && isNavigation());
    const displayLoadingCards = (isApiLoading && !showErrorComponent);
    const displayCarousel = (!isApiLoading && !showErrorComponent);
    const displayPaginationMenu = (items.length > 1 && isNavigation());
    const carouselWrapperClass = cardNumberClass(itemstoshow > items.length ? items.length : itemstoshow);

    return (

        <ComponentBackground
            componentId={"Carousel-" + componentId}
            bgImage={componentBGImage}
            bgMobileImage={componentBGMobileImage}
            bgImgOpacity={bgImgOpacity}
            backGround={backGround}
            setLightBg={setLightBg}
            viewName={carouselType}
        >
            <article>

                <div className="container px-0 rel">

                    <ComponentHeader
                        disptitle={componentTitle}
                        dispdescription={componentDescription}
                        light={lightBg}
                        shortMargin={link.url ? true : false}
                    />

                    {
                        link.url &&
                        < CustomLinkButton
                            pageUrl={""}
                            link={link}
                            isBtn={false}
                            btnClass={""}
                            linkClass="link-btn for-desk"
                            dir={dir}
                            linkIcon={true}
                            charLimit={25}
                        />
                    }

                    <ErrorMsgComponent show={showErrorComponent} text={apiErrorMsg} />

                    <LoadingCardsComponent show={displayLoadingCards} items={loadingItems} />

                    {
                        displayCarousel &&

                        <div className={`rel mst-swiper ${carouselWrapperClass}`}>

                            <swiper-container
                                init={false}
                                class={`mst-swiper-${componentId}`}
                                id={`mst-swiper-${componentId}`} >

                                {
                                    items.map((item, idx) => <swiper-slide
                                        class="swiper-slide"
                                        key={idx}>

                                            <CarouselItemComponent
                                                carouselItem={item}
                                                carouselType={carouselType}
                                                dir={dir}
                                                getIconsHasImages={getIconsHasImages}
                                                getLinksHasImages={getLinksHasImages}
                                                isApiList={isApiList}
                                                isflipAll={flipAll}
                                                setFlipAll={setFlipAll}
                                                title={componentTitle}
                                                typeOfEventCarousel={getEventCarouselType(items)}
                                            />

                                        </swiper-slide>
                                    )
                                }

                            </swiper-container>

                                               
                            <NavigationBtns
                                dir={dir}
                                componentId={componentId}
                                show={displayCarouselNavigationBtns}
                            />

                        </div>
                    }

                    <PaginationMenu
                        show={displayCarousel && displayPaginationMenu}
                        componentId={componentId}
                        dir={dir}
                        isAutoplayActive={isAutoplayActive}
                        isMiniNews={carouselType === CAROUSEL_TYPES.MINI_NEWS}
                        isTablet={isTablet}
                        pauseNews={pauseNews}
                        link={link}
                    />

                </div>

            </article>

        </ComponentBackground>
    )
        
}

var elements = document.querySelectorAll('[id^="Carousel-"]');

for (var i = 0; i < elements.length; i++) {
    var rootElement = document.getElementById(elements[i].id);
    var root = createRoot(rootElement);

    root.render(
        <Carousel
            componentTitle={rootElement.getAttribute('component-title')}
            componentDescription={rootElement.getAttribute('component-description')}
            componentQuery={rootElement.getAttribute('component-query')}
            itemstoshow={+rootElement.getAttribute('component-itemstoshow')}
            currentPageId={+rootElement.getAttribute('component-currentPageId')}
            componentId={rootElement.getAttribute('component-id')}
            light={rootElement.getAttribute('component-light')}
            componentBGImage={rootElement.getAttribute('component-bgImage')}
            componentBGMobileImage={rootElement.getAttribute('component-bgMobileImage')}
            bgImgOpacity={rootElement.getAttribute('component-bgImgOpacity')}
            backColor={rootElement.getAttribute('component-backColor')}
            carouselType={rootElement.getAttribute('component-type')}
            link={rootElement.getAttribute('component-link')}
        />)
}